import { ReactNode, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import Icon from '../../atoms/Icon'
import { IconPack } from '../../atoms/Icon/enum'

export interface Props {
  color?: 'gray' | 'orange' | 'yellow'
  size?: 'medium' | 'small'
  beforeIcon?: IconPack
  afterIcon?: IconPack
  children: ReactNode
  className?: string
}

const Badge: React.FC<Props> = ({
  color = 'gray',
  size = 'medium',
  beforeIcon,
  afterIcon,
  children,
  className,
}: Props): JSX.Element => {
  const [iconSize, setIconSize] = useState(12)

  useEffect(() => {
    setIconSize(size === 'medium' ? 12 : 10)
  }, [size])

  return (
    <Wrapper color={color} size={size} className={className}>
      {beforeIcon && <Icon name={beforeIcon} width={iconSize} height={iconSize} />}
      {children && (
        <StyledText $beforeIcon={beforeIcon} $afterIcon={afterIcon}>
          {children}
        </StyledText>
      )}
      {afterIcon && <Icon name={afterIcon} width={iconSize} height={iconSize} />}
    </Wrapper>
  )
}

const Wrapper = styled.span<Props>`
  max-width: 100%;
  box-sizing: border-box;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;

  > * {
    flex-shrink: 0;
  }

  ${({ color }) => {
    switch (color) {
      case 'gray':
        return css`
          background-color: #f5f5f5;
          color: #000000;
        `
      case 'orange':
        return css`
          background-color: #ffede3;
          color: #ff540f;
        `
      case 'yellow':
        return css`
          background-color: #fff7e0;
          color: #bf8650;
        `
    }
  }}
  ${({ size }) => {
    switch (size) {
      case 'medium':
        return css`
          padding: 6px 10px;
          font-size: 12px;
        `
      case 'small':
        return css`
          padding: 4px 6px;
          font-size: 9px;
        `
    }
  }}
`

const iconWidth = '12px'
const marginWidth = '3px'
const StyledText = styled.span<{ $beforeIcon: IconPack | undefined; $afterIcon: IconPack | undefined }>`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  ${({ $beforeIcon, $afterIcon }) =>
    ($beforeIcon || $afterIcon) && `max-width: calc(100% - ${iconWidth} - ${marginWidth});`}
  ${({ $beforeIcon }) => $beforeIcon && 'margin-left: 3px;'}
  ${({ $afterIcon }) => $afterIcon && 'margin-right: 3px;'}
`

export default Badge
